import { useTranslation } from 'react-i18next';
import Badge from '@/components/base/Badge';
import DataTable, { type Column } from '@/components/base/DataTable';
import { formatCurrency, formatDateTime } from '@/lib/format';
import type { DriverTask } from '@/types';

interface DriverTasksTableProps {
  tasks: DriverTask[];
  currency: string;
}

function TypeBadge({ type }: { type: DriverTask['type'] }) {
  const { t } = useTranslation();
  return (
    <Badge variant={type === 'pickup' ? 'accent' : 'primary'} icon={type === 'pickup' ? 'ri-inbox-archive-line' : 'ri-send-plane-line'}>
      {t(`drivers.task.${type}`)}
    </Badge>
  );
}

function FeeCell({ value, currency }: { value: number; currency: string }) {
  return <span className="lb-ltr-field text-sm font-semibold text-foreground-900">{formatCurrency(value, currency)}</span>;
}

export default function DriverTasksTable({ tasks, currency }: DriverTasksTableProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const columns: Column<DriverTask>[] = [
    { key: 'type', header: t('drivers.detail.tasks.type'), render: (task) => <TypeBadge type={task.type} /> },
    {
      key: 'order',
      header: t('drivers.detail.tasks.order'),
      render: (task) => <span className="lb-ltr-field text-sm text-foreground-800">{task.orderId}</span>,
    },
    {
      key: 'date',
      header: t('drivers.detail.tasks.date'),
      render: (task) => <span className="text-sm text-foreground-700">{formatDateTime(task.completedAt, lang)}</span>,
    },
    {
      key: 'fee',
      header: t('drivers.detail.tasks.fee'),
      className: 'text-end',
      render: (task) => (
        <div className="flex justify-end">
          <FeeCell value={task.deliveryFee} currency={currency} />
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="hidden md:block">
        <DataTable columns={columns} rows={tasks} rowKey={(task) => task.id} />
      </div>

      <ul className="divide-y divide-background-100 md:hidden">
        {tasks.map((task) => (
          <li key={task.id} className="space-y-2 p-4">
            <div className="flex items-center justify-between gap-3">
              <TypeBadge type={task.type} />
              <span className="lb-ltr-field text-sm font-semibold text-foreground-900">
                {formatCurrency(task.deliveryFee, currency)}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-hashtag" aria-hidden="true" />
                <span className="lb-ltr-field text-foreground-700">{task.orderId}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true" />
                {formatDateTime(task.completedAt, lang)}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}